import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'motion/react';
import { cn } from '@shared/lib';
import { PlainField } from '@shared/ui';
import type { Level } from '@shared/types';
import type { LookupResult, SaveManualCardArgs } from '../api/workspaceApi';
import type { WorkspaceCard } from '../model';
import { buildLookupView, isLocked } from '../lib/senseChoice';
import { useTranslationLookup } from '../lib/useTranslationLookup';
import { CompactLookup } from './CompactLookup';

import { CreationModeTabs, MAX_TRANSLATION_CHARS, type CreationMode } from './ManualMode';

/** Императивный хендл — родитель (ListScreen) гасит фокус по тапу на затемнение. */
export interface ComposeBarHandle {
  blur: () => void;
  /** После создания на шаге 2 — очистить поле (слово уже стало карточкой). */
  reset: () => void;
}

interface ComposeBarProps {
  moduleId: string | null;
  moduleTitle: string;
  /** Уровень для AI-карточки (UI скрыт — всегда B1, как в форме модуля). */
  difficulty: Level;
  mode: CreationMode;
  onModeChange: (m: CreationMode) => void;
  onSubmitManual: (args: SaveManualCardArgs) => Promise<WorkspaceCard | null>;
  /** Шаг 2: плашка «Выбрать, что учить» или Enter открывают выбор переводов. */
  onChoose: (args: { front: string; data: LookupResult }) => void;
  /** Фокус инпута → родитель схлопывает шапку и затемняет список (оверлей-режим поиска). */
  onFocusedChange: (focused: boolean) => void;
}

function SearchIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <circle cx="11" cy="11" r="7" />
      <path d="M21 21l-4-4" />
    </svg>
  );
}
function ArrowIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <path d="M5 12h13M12 5l7 7-7 7" />
    </svg>
  );
}
function ChevDown({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <path d="M4 6l4 4 4-4" />
    </svg>
  );
}

/**
 * Верхний композер мобильного экрана создания карточек. Живёт в sticky-шапке (не снизу):
 * тоггл AI/Вручную + поле ввода + стрелка отправки (только ручная карточка с переводом) +
 * компактный дропдаун словарного поиска. При фокусе сообщает наверх (onFocusedChange) —
 * родитель схлопывает шапку и затемняет список, имитируя полноэкранный режим поиска.
 */
export const ComposeBar = forwardRef<ComposeBarHandle, ComposeBarProps>(function ComposeBar(
  { moduleId, moduleTitle, difficulty, mode, onModeChange, onSubmitManual, onChoose, onFocusedChange },
  ref,
) {
  const { t } = useTranslation();
  const [front, setFront] = useState('');
  const [back, setBack] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [focused, setFocused] = useState(false);
  const [noteOpen, setNoteOpen] = useState(false);

  const frontRef = useRef<HTMLDivElement>(null);
  // Второй инпут ручной карточки — перевод (подсказки словаря подставляются сюда).
  const backRef = useRef<HTMLDivElement>(null);
  const lastQ = useRef(front.trim().toLowerCase());

  const isManual = mode === 'manual';

  // Живой словарный поиск — только в AI-режиме: превью шага 1. Ручная карточка —
  // только «слово → свой перевод», поиска по словарю там нет (решение 2026-09-25).
  const lookup = useTranslationLookup(front, moduleId, !isManual);
  // Смена слова: подставленный перевод принадлежал старому слову — очищаем.
  useEffect(() => {
    const q = front.trim().toLowerCase();
    if (lastQ.current !== q) {
      lastQ.current = q;
      setBack('');
    }
  }, [front]);
  // Можно ли перейти к выбору: слово найдено и хоть один перевод ещё не учится.
  const choosable =
    lookup.status === 'found' && buildLookupView(lookup.data).all.some((x) => !isLocked(x));
  // Превью — только в фокусе и только в AI-режиме (после blur инпут «встаёт на место»).
  const popOpen = !isManual && focused && front.trim().length >= 2 && lookup.status !== 'idle';
  // AI: карточка создаётся только через выбор переводов — плашка «Выбрать, что учить»
  // или Enter. Стрелки у поля нет: она спорила с плашкой (решение владельца 2026-09-26).
  const canChoose = choosable && front.trim().length > 0 && moduleId != null;
  const manualValid = front.trim().length > 0 && back.trim().length > 0 && moduleId != null;
  // Стрелка отправки — только у ручной карточки.
  const sendEnabled = isManual && manualValid;

  useEffect(() => {
    onFocusedChange(focused);
  }, [focused, onFocusedChange]);

  useImperativeHandle(
    ref,
    () => ({
      blur: () => {
        frontRef.current?.blur();
        backRef.current?.blur();
      },
      reset: () => {
        setFront('');
        setBack('');
      },
    }),
    [],
  );

  const handleFocus = () => setFocused(true);
  // Не теряем «фокус-режим» при переходе между полем слова и полем перевода.
  const handleBlur = () => {
    requestAnimationFrame(() => {
      const a = document.activeElement;
      if (a !== frontRef.current && a !== backRef.current) setFocused(false);
    });
  };

  const doSubmit = async () => {
    if (!isManual) {
      if (canChoose && lookup.status === 'found') {
        // Шаг 2 — выбор переводов; клавиатуру прячем, экран выбора её не требует.
        frontRef.current?.blur();
        onChoose({ front: front.trim(), data: lookup.data });
      }
      return;
    }
    if (!sendEnabled || submitting) return;
    setSubmitting(true);
    try {
      const fresh = await onSubmitManual({
        front: front.trim(),
        // Ровно один перевод: словарь — только подсказка (см. ManualSuggestions).
        translations: [back.trim()],
        moduleId: moduleId!,
        moduleTitle,
        difficulty,
      });
      if (fresh) {
        setFront('');
        setBack('');
        frontRef.current?.focus();
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Слова нет в словаре → ручной режим. front сохраняется, фокусируем поле перевода.
  const switchToManual = () => {
    onModeChange('manual');
    requestAnimationFrame(() => backRef.current?.focus());
  };

  const blurAll = () => {
    frontRef.current?.blur();
    backRef.current?.blur();
  };

  return (
    <div className="px-3 pb-2.5 pt-0.5">
      {/* Тоггл AI/Вручную + «Скрыть» (в фокусе). В фокусе композер у самого верха —
          отодвигаем вправо ТОЛЬКО узкий ряд тогла, чтобы он не лез под фиксированный
          гамбургер сайдбара (left-3 top-2); инпут и дропдаун остаются на всю ширину. */}
      <div
        className={cn(
          'mb-2.5 flex items-center gap-2 transition-[padding] duration-200',
          focused && 'pl-11',
        )}
      >
        <CreationModeTabs
          mode={mode}
          onChange={onModeChange}
          labels={{ ai: t('cardsWorkspace.compose.ai'), manual: t('cardsWorkspace.compose.manual') }}
        />
        <div className="flex-1" />
        {focused && (
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={blurAll}
            className="inline-flex items-center gap-1 rounded-pill border border-line px-2.5 py-1 text-[12px] font-medium text-muted active:bg-elev"
          >
            <ChevDown className="h-3 w-3" /> {t('cardsWorkspace.compose.hide')}
          </button>
        )}
      </div>

      {/* строка ввода: поиск-иконка + инпут + стрелка (только ручная карточка с переводом) */}
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 rounded-[14px] border border-line bg-card px-3 transition-colors focus-within:border-accent">
            {!isManual && <SearchIcon className="h-[18px] w-[18px] shrink-0 text-muted-2" />}
            <PlainField
              ref={frontRef}
              value={front}
              onChange={setFront}
              onFocus={handleFocus}
              onBlur={handleBlur}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || (e.key === 'Tab' && isManual)) {
                  e.preventDefault();
                  if (!isManual || (e.key === 'Enter' && manualValid)) void doSubmit();
                  else backRef.current?.focus();
                }
              }}
              placeholder={t('cardsWorkspace.compose.wordPlaceholder')}
              ariaLabel={t('cardsWorkspace.compose.wordPlaceholder')}
              className="w-full bg-transparent py-3 font-serif text-[18px] uppercase tracking-[0.01em] text-fg outline-none"
              autoCapitalize="characters"
              spellCheck={false}
            />
          </div>
        </div>

        <AnimatePresence initial={false}>
          {sendEnabled && (
            <motion.button
              key="send"
              type="button"
              initial={{ opacity: 0, scale: 0.8, width: 0, marginLeft: -8 }}
              animate={{ opacity: 1, scale: 1, width: 50, marginLeft: 0 }}
              exit={{ opacity: 0, scale: 0.8, width: 0, marginLeft: -8 }}
              transition={{ duration: 0.16, ease: 'easeOut' }}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => void doSubmit()}
              disabled={submitting}
              className="grid h-[50px] shrink-0 place-items-center overflow-hidden rounded-[14px] bg-accent text-fg-inv transition-transform active:scale-95 disabled:opacity-60"
              aria-label={t('cardsWorkspace.compose.hintManual')}
            >
              <ArrowIcon className="h-[20px] w-[20px]" />
            </motion.button>
          )}
        </AnimatePresence>
      </div>

      {/* второй инпут ручной карточки — перевод; подсказки из попа подставляются сюда */}
      {isManual && (
        <div className="mt-2 flex items-center gap-2 rounded-[14px] border border-line bg-card px-3 transition-colors focus-within:border-accent">
          <span className="shrink-0 text-[12px] font-semibold uppercase tracking-[0.14em] text-muted-2">
            {t('cardsWorkspace.manual.translationLabel')}
          </span>
          <PlainField
            ref={backRef}
            value={back}
            onChange={(v) => setBack(v.slice(0, MAX_TRANSLATION_CHARS))}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                void doSubmit();
              }
            }}
            placeholder={t('cardsWorkspace.manual.translationPlaceholder')}
            ariaLabel={t('cardsWorkspace.manual.translationLabel')}
            className="w-full bg-transparent py-3 font-serif text-[17px] text-fg outline-none"
            spellCheck={false}
          />
        </div>
      )}

      {/* шаг 1: превью главных переводов + плашка перехода к выбору (только AI) */}
      <AnimatePresence initial={false}>
        {popOpen && (
          <motion.div
            key="lookup"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{
              height: { duration: 0.22, ease: [0.2, 0.9, 0.2, 1] },
              opacity: { duration: 0.14, ease: 'easeOut' },
            }}
            className="overflow-hidden"
          >
            <div className="mt-2.5 max-h-[max(44dvh,calc(100dvh_-_10rem))] overflow-y-auto overscroll-contain">
              <CompactLookup state={lookup} onChoose={() => void doSubmit()} onManual={switchToManual} />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* заметка про ограничения ручной карточки */}
      <AnimatePresence initial={false}>
        {isManual && focused && (
          <motion.div
            key="manual-note"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{
              height: { duration: 0.26, ease: [0.2, 0.9, 0.2, 1] },
              opacity: { duration: 0.18, ease: 'easeOut' },
            }}
            className="overflow-hidden"
          >
            <div className="mt-2.5 overflow-hidden rounded-[13px] border border-warn/25 bg-warn/[0.06]">
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => setNoteOpen((o) => !o)}
                className="flex w-full items-center gap-2.5 px-3 py-2.5 text-left"
              >
                <span className="grid h-[18px] w-[18px] shrink-0 place-items-center rounded-full bg-warn/20 text-[12px] font-bold text-warn">
                  i
                </span>
                <span className="flex-1 text-[12px] font-medium text-fg">
                  {t('cardsWorkspace.compose.manualNote')}
                </span>
                <ChevDown className={cn('h-3 w-3 text-warn transition-transform', noteOpen && 'rotate-180')} />
              </button>
              <AnimatePresence initial={false}>
                {noteOpen && (
                  <motion.div
                    key="note-detail"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2, ease: [0.2, 0.9, 0.2, 1] }}
                    className="overflow-hidden"
                  >
                    <div className="px-3 pb-3 pl-[39px] text-[12px] leading-relaxed text-muted">
                      {t('cardsWorkspace.compose.manualNoteDetail')}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
});
